import _ from 'lodash';

/**
 * Open and close sh-att-view to show post attachment previews
 */
export class AttService {
	/**
	 * @param {$compile} $compile	Compiles an HTML string or DOM into a template and produces a template function, which can then be used to link scope and the template together.
	 * @param {$rootScope} $rootScope  Used to create a new scope for <sh-att-listing-popup>
	 * @param {Api} Api  Used to get attachment info
	 * @param {modal} modal  Used to popup errors
	 * @returns {undefined}
	 */
	constructor($compile, $rootScope, Api, modal, UrlData) {
		'ngInject';
		this.$compile = $compile;
		this.$rootScope = $rootScope;
		this.Api = Api;
		this.modal = modal;
		this.UrlData = UrlData;
	}

	/**
	 * Based on the extension of the given file, determine what kind of viewer to use
	 * @param {Object} file  File with properties icon and extension
	 * @returns {string}
	 */
	getViewerType(file) {
		const viewers = [
			{
				type: 'prizm',
				icons: ['ppt', 'doc', 'pdf', 'xls'],
				exts: ['psd', 'ai', 'eml', 'msg'],
			},
			{ type: 'video', exts: ['mp4', 'm4v'] },
			{ type: 'image', exts: ['gif', 'png', 'jpg', 'jpeg', 'svg'] },
			{ type: 'audio', exts: ['wav', 'mp3', 'flac'] },
		];
		const viewer = _.find(viewers, viewer => {
			return (
				(viewer.icons && viewer.icons.indexOf(file.icon) > -1) ||
				(viewer.exts && viewer.exts.indexOf(String(file.extension).toLowerCase()) > -1)
			);
		});
		return viewer ? viewer.type : 'other';
	}

	/**
	 * Get the text for what kind of action the popup will create.
	 * At small screen sizes, the popup contains thumbnails and links to download.
	 * At larger screen sizes, the popup also has a right pane to preview files.
	 * @returns {string}
	 */
	getActionText() {
		return document.documentElement.clientWidth < 768 ? 'Details' : 'Preview';
	}

	/**
	 * Opens preview of attachments found in `post`
	 * @param {Object} post  The post whose attachments will be previewed
	 * @params {Object} [options={}]
	 * @property {Number} [startIndex=0]  The index of the attachment to select first
	 * @property {String} [searchTerm=""]  A default search term to show in the document
	 * @returns {Promise}
	 */
	popupPost(post, options = {}) {
		// TODO: fullscreen loading
		let popupPost = {};
		this.Api.get(`/v3/posts/${post.id}/attachments/details`).then(attachments => {
			if (attachments && attachments.length) {
				const popupListing = this.popupListing(attachments, {
					titleText: post.headline,
					startIndex: options.startIndex,
					searchTerm: options.searchTerm,
				});
				_.assign(popupPost, popupListing);
			} else {
				return this.modal.alert({ text: 'Error: no files found' });
			}
		});
		return popupPost;
	}

	/**
	 * Opens preview of a list of files, looking up synonyms in the vocabulary trainer
	 * @param {Object[]} attachments  The attachments to preview
	 * @params {Object} [options={}]
	 * @property {Number} [titleText="File details"]  The text to display in the title area
	 * @property {Number} [startIndex=0]  The index of the attachment to select first
	 * @property {String} [searchTerm=""]  A default search term to show in the document
	 * @returns {Promise<Scope>}  Promise resolving with the scope of the popup element
	 */
	popupListing(attachments, options = {}) {
		if (!options.searchTerm) {
			options.searchTerm = (this.UrlData.get('finetune') || {}).term;
			//Check if the term is in the url via regular query params (done when coming from a react page)
			if (!options.searchTerm) {
				const params = new URLSearchParams(window.location.search);
				options.searchTerm = params.get('term');
			}
		}
		if (_.isString(options.searchTerm)) {
			const phrase = options.searchTerm;
			return this.Api.get('/attachments/prizm_search_terms', { phrase }).then(termInfo => {
				options.searchTerm = termInfo;
				return this.insertAttListingComponent(attachments, options);
			});
		} else {
			return Promise.resolve(this.insertAttListingComponent(attachments, options));
		}
	}

	/**
	 * Inserts <sh-att-list-popup> component to show preview of a list of files
	 * @param {Object[]} attachments  The attachments to preview
	 * @params {Object} [options]
	 * @property {Number} [titleText="File details"]  The text to display in the title area
	 * @property {Number} [startIndex=0]  The index of the attachment to select first
	 * @property {String} [searchTerm=""]  A default search term to show in the document
	 * @returns {Object}  The scope of the popup element
	 */
	insertAttListingComponent(attachments, options) {
		const { titleText, startIndex, searchTerm } = options;
		const $scope = this.$rootScope.$new();
		$scope.attachments = attachments;
		$scope.searchTerm = searchTerm;
		$scope.startIndex = startIndex || 0;
		$scope.titleText = titleText || 'File details';
		$scope.close = () => {
			element.remove();
			$scope.$destroy();
			document.documentElement.style.overflow = oldOverflow;
		};
		let element = angular.element(`
			<sh-att-list-popup
				attachments="attachments"
				search-term="searchTerm"
				start-index="startIndex"
				title-text="titleText"
				on-close="close()"
			></sh-att-list-popup>
		`);
		element = this.$compile(element)($scope);
		angular.element(document.body).append(element);
		const oldOverflow = document.documentElement.style.overflow;
		document.documentElement.style.overflow = 'hidden';
		return $scope;
	}

	/**
	 * Opens preview of the file with the given id
	 * @param {Number} fileId
	 * @params {Object} [options={}]
	 * @property {Number} [titleText="File details"]  The text to display in the title area
	 * @property {String} [searchTerm=""]  A default search term to show in the document
	 * @returns {Promise}
	 */
	popupId(fileId, options = {}) {
		// TODO: fullscreen loading
		return this.Api.get(`/v3/files/${fileId}`).then(file => {
			return this.popupListing([file], options);
		});
	}

	/**
	 * Opens a list of attachments with links to download
	 * @param {Number} postId  The id of the post to get attachments for
	 * @returns {Promise}  Fulfilled when popup is closed
	 */
	popupDownloadList(postId) {
		return this.modal
			.state('hub.loggedin.PostAttList', {
				params: { id: postId },
				heading: 'Download',
				width: 960,
				height: 'auto',
			})
			.catch(() => {});
	}
}
